import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'components/UIElements'
import { browserHistory } from 'utils/navigation'
import {
  MODAL_BUTTONS_MAP,
  MODAL_CLASSES_MAP,
} from 'utils/constants'
import {
  getScheduleExpireValue,
  getScheduleFrequencyValue,
  getScheduleSendToValue,
  getScheduleStartTypeLabel,
  getScheduleStartValue,
  getScheduleVisibilityValue,
  normalizeSchedules,
} from '../utils/schedulePreviewHelpers'
import { getScheduleId } from '../../../../../utils/multiSchedule'

const DETAIL_FIELDS = [
  {
    key: 'start',
    label: 'Start',
    iconClass: 'far fa-clock',
    iconTone: 'start',
    getValue: (schedule, metadata, _cohortMap, _siteSelectionList, nameContext) =>
      getScheduleStartValue(schedule, metadata?.article_type, metadata?.deploy_on_confirm, nameContext),
  },
  {
    key: 'expire',
    label: 'Expire',
    iconClass: 'far fa-calendar-alt',
    iconTone: 'expire',
    getValue: schedule => getScheduleExpireValue(schedule),
  },
  {
    key: 'frequency',
    label: 'Frequency',
    iconClass: 'far fa-clock',
    iconTone: 'frequency',
    getValue: schedule => getScheduleFrequencyValue(schedule),
  },
  {
    key: 'visibility',
    label: 'Visibility',
    iconClass: 'far fa-eye',
    iconTone: 'visibility',
    getValue: (schedule, metadata) => getScheduleVisibilityValue(schedule, metadata),
  },
  {
    key: 'sendTo',
    label: 'Send to',
    iconClass: 'fas fa-users',
    iconTone: 'send-to',
    getValue: (schedule, _metadata, cohortMap, siteSelectionList) =>
      getScheduleSendToValue(schedule, cohortMap, siteSelectionList),
  },
]

const ScheduleItem = ({
  schedule,
  index,
  metadata,
  cohortMap,
  siteSelectionList,
  nameContext,
  showActions,
  canDelete,
  isReadOnlyState,
  onEditSchedule,
  onDeleteSchedule,
}) => {
  const [open, setOpen] = useState(false)
  const startTypeLabel = getScheduleStartTypeLabel(schedule)
  const scheduleTitle = `Schedule ${index + 1}`

  return (
    <div className={`schedule-item${open ? ' open' : ''}`}>
      <button
        type='button'
        className='schedule-item-header'
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}>
        <span className='schedule-item-title'>
          <i
            className={`fas ${open ? 'fa-caret-down' : 'fa-caret-right'} schedule-item-chevron`}
            aria-hidden='true'
          />
          <span className='schedule-item-name'>{scheduleTitle}</span>
        </span>
        <span className='schedule-type-tag'>
          <i className='far fa-clock' aria-hidden='true' />
          <span>{startTypeLabel}</span>
        </span>
      </button>
      {open && (
        <div className='schedule-item-body'>
          <div className='schedule-details-grid'>
            {DETAIL_FIELDS.map(field => (
              <div key={field.key} className='schedule-detail'>
                <span className={`schedule-detail-icon ${field.iconTone}`}>
                  <i className={field.iconClass} aria-hidden='true' />
                </span>
                <div className='schedule-detail-text'>
                  <span className='schedule-detail-label'>{field.label}</span>
                  <span className='schedule-detail-value'>
                    {field.getValue(schedule, metadata, cohortMap, siteSelectionList, nameContext)}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {showActions && (
            <div className='schedule-item-actions'>
              {canDelete && (
                <Button
                  id={`delete-schedule-${index}`}
                  className='no-margin schedule-action-btn schedule-delete-btn'
                  content='Delete Schedule'
                  onClick={e => {
                    e.stopPropagation()
                    onDeleteSchedule(schedule, index)
                  }}
                />
              )}
              <Button
                id={`${isReadOnlyState ? 'view' : 'edit'}-schedule-${index}`}
                className='no-margin schedule-action-btn schedule-edit-btn'
                content={isReadOnlyState ? 'View Schedule' : 'Edit Schedule'}
                onClick={e => {
                  e.stopPropagation()
                  onEditSchedule(schedule, index)
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

ScheduleItem.propTypes = {
  schedule: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  metadata: PropTypes.object,
  cohortMap: PropTypes.objectOf(PropTypes.string),
  siteSelectionList: PropTypes.array,
  nameContext: PropTypes.object,
  showActions: PropTypes.bool,
  canDelete: PropTypes.bool,
  isReadOnlyState: PropTypes.bool,
  onEditSchedule: PropTypes.func,
  onDeleteSchedule: PropTypes.func,
}

const SchedulePreview = ({
  instrument,
  confirmedSchedules,
  cohortMap,
  siteSelectionList,
  nameContext,
  studyID,
  studyVersionID,
  closeDrawer,
  canWrite,
  studyLock,
  isReadOnlyState,
  hasMultiSchedule = false,
  loadScheduleForEdit,
  deleteConfirmedSchedule,
  openModal,
  closeModal,
}) => {
  const metadata = instrument?.metadata || {}
  // Prefer confirmedSchedules (full multi-schedule list). metadata.schedule is collapsed to
  // the active editing object after fetch, so normalizeSchedules alone would show only 1.
  const schedules =
    Array.isArray(confirmedSchedules) && confirmedSchedules.length > 0
      ? confirmedSchedules
      : normalizeSchedules(metadata.schedule)
  const scheduleCount = schedules.length
  const canEditSchedules = !!(canWrite && !studyLock && !isReadOnlyState && studyID && studyVersionID && instrument?.id)
  const showActions = !!(studyID && studyVersionID && instrument?.id && (canEditSchedules || isReadOnlyState))
  const canDelete = !!(hasMultiSchedule && canEditSchedules && scheduleCount > 1)

  const goToEditSchedule = (_schedule, index) => {
    // Load selected schedule into the editor before navigating (same as All Schedules).
    if (typeof loadScheduleForEdit === 'function') {
      loadScheduleForEdit(index)
    }
    if (closeDrawer) closeDrawer()
    browserHistory.push(`/studies/${studyID}/${studyVersionID}/instruments/${instrument.id}/schedule?scheduleIndex=${index}`)
  }

  const onDeleteSchedule = (_schedule, index) => {
    const scheduleName = `Schedule ${index + 1}`
    openModal({
      content: `Are you sure you want to delete ${scheduleName}? This cannot be undone.`,
      className: MODAL_CLASSES_MAP.confirmation,
      confirmButton: MODAL_BUTTONS_MAP.confirm,
      cancelButton: MODAL_BUTTONS_MAP.cancel,
      onConfirm: () => {
        Promise.resolve(deleteConfirmedSchedule(index))
          .then(() => closeModal())
          .catch(() => closeModal())
      },
    })
  }

  if (!scheduleCount) {
    return (
      <div className='schedule-preview'>
        <p className='schedule-empty'>No schedule configured for this instrument.</p>
      </div>
    )
  }

  return (
    <div className='schedule-preview'>
      {scheduleCount > 1 && (
        <div className='schedule-info-banner'>
          <p>
            This instrument has {scheduleCount} schedules. You can expand each schedule to view details
            or edit settings.
          </p>
        </div>
      )}
      <div className='schedule-list'>
        {schedules.map((schedule, index) => (
          <ScheduleItem
            key={getScheduleId(schedule) || `schedule-${index}`}
            schedule={schedule}
            index={index}
            metadata={metadata}
            cohortMap={cohortMap}
            siteSelectionList={siteSelectionList}
            nameContext={nameContext}
            showActions={showActions}
            canDelete={canDelete}
            isReadOnlyState={isReadOnlyState}
            onEditSchedule={goToEditSchedule}
            onDeleteSchedule={onDeleteSchedule}
          />
        ))}
      </div>
    </div>
  )
}

SchedulePreview.propTypes = {
  instrument: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    metadata: PropTypes.object,
  }),
  confirmedSchedules: PropTypes.arrayOf(PropTypes.object),
  cohortMap: PropTypes.objectOf(PropTypes.string),
  siteSelectionList: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
  ),
  nameContext: PropTypes.object,
  studyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  studyVersionID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  closeDrawer: PropTypes.func,
  canWrite: PropTypes.bool,
  studyLock: PropTypes.bool,
  isReadOnlyState: PropTypes.bool,
  hasMultiSchedule: PropTypes.bool,
  loadScheduleForEdit: PropTypes.func,
  deleteConfirmedSchedule: PropTypes.func,
  openModal: PropTypes.func,
  closeModal: PropTypes.func,
}

export default SchedulePreview
